/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React from "react";
import { Image, Card, Container, Navbar, Row, Col } from "react-bootstrap";
import { HashLink } from "react-router-hash-link";
import moment from "moment-timezone";

export interface IFooterLinkProps {
    name: string;
    link: string;
    target?: "_blank" | "_self";
}

export interface IFooterProps {
    footerBrandLink?: string;
    footerBrandLogo: string;
    footerBrandTitle: string;
    footerBrandTitleStyle?: any;
    footerBrandDescription?: string;
    footerUsefulLinkLabel?: string;
    footerUsefulLinks?: IFooterLinkProps[];
    footerServiceLinkLabel?: string;
    footerServiceLinks?: IFooterLinkProps[];
    footerContactLabel?: string;
    footerContactLinks?: {
        name: string;
        value: string;
    }[];
    footerCopyright: {
        brandTitle: string;
        brandLogo: string;
        brandLogoHeight?: number;
        brandLink: string;
        copyrightTitle: string;
        startYear: string;
        target?: "_blank" | "_self";
    };
}

export const Footer = ({
    footerBrandLogo,
    footerBrandDescription,
    footerBrandLink,
    footerBrandTitle,
    footerBrandTitleStyle,
    footerUsefulLinkLabel,
    footerUsefulLinks,
    footerServiceLinks,
    footerServiceLinkLabel,
    footerContactLabel,
    footerContactLinks,
    footerCopyright
}: IFooterProps) => {
    const currentYear = moment().get("year");
    const _renderBrand = () => {
        return (
            <Col md={5}>
                <Navbar.Brand
                    as={HashLink}
                    to={footerBrandLink}
                    className="me-lg-3 mb-3 d-flex align-items-center"
                >
                    <Image src={footerBrandLogo} />
                    <span
                        className="ms-2 brand-text"
                        style={footerBrandTitleStyle}
                    >
                        {footerBrandTitle}
                    </span>
                </Navbar.Brand>
                {footerBrandDescription && <p>{footerBrandDescription}</p>}
            </Col>
        );
    };

    const _renderUsefulLinks = () => {
        return (
            <Col xs={6} md={2} className="mb-5 mb-lg-0">
                {footerUsefulLinkLabel && (
                    <span className="h5">{footerUsefulLinkLabel}</span>
                )}
                {footerUsefulLinks && (
                    <ul className="links-vertical mt-2">
                        {footerUsefulLinks?.map((item, index) => (
                            <li key={`footer-list-${index}`}>
                                <Card.Link
                                    target={item?.target ? "_blank" : "_self"}
                                    href={item?.link}
                                >
                                    {item?.name}
                                </Card.Link>
                            </li>
                        ))}
                    </ul>
                )}
            </Col>
        );
    };

    const _renderServiceLinks = () => {
        return (
            <Col xs={6} md={2} className="mb-5 mb-lg-0">
                {footerServiceLinkLabel && (
                    <span className="h5">{footerServiceLinkLabel}</span>
                )}
                {footerServiceLinks && (
                    <ul className="links-vertical mt-2">
                        {footerServiceLinks?.map((item, index) => (
                            <li key={`footer-list-${index}`}>
                                <Card.Link
                                    target={item?.target ? "_blank" : "_self"}
                                    href={item?.link}
                                >
                                    {item?.name}
                                </Card.Link>
                            </li>
                        ))}
                    </ul>
                )}
            </Col>
        );
    };

    const _renderAddress = () => {
        return (
            <Col xs={6} md={3} className="mb-5 mb-lg-0">
                {footerContactLabel && (
                    <span className="h5">{footerContactLabel}</span>
                )}
                {footerContactLinks && (
                    <ul className="links-vertical mt-2">
                        {footerContactLinks?.map((item, index) => (
                            <li key={`footer-list-${index}`}>
                                {item?.name} {item?.value}
                            </li>
                        ))}
                    </ul>
                )}
            </Col>
        );
    };

    const _renderHorizontal = () => {
        return <hr className="bg-gray-600 my-5" />;
    };

    const _renderFooterCopyRight = () => {
        return (
            <Row>
                <Col className="mb-md-2">
                    <Card.Link
                        href={footerCopyright?.brandLink}
                        target={footerCopyright?.target ?? "_self"}
                        className="d-flex justify-content-center"
                    >
                        <Image
                            src={footerCopyright?.brandLogo}
                            height={footerCopyright?.brandLogoHeight ?? 25}
                            className="d-block mx-auto mb-3"
                        />
                    </Card.Link>
                    <div
                        className="d-flex text-center justify-content-center align-items-center"
                        role="contentinfo"
                    >
                        <p className="font-weight-normal font-small mb-0">
                            {`Copyright © ${footerCopyright?.copyrightTitle} ${footerCopyright?.startYear}-`}
                            <span className="current-year">{currentYear}</span>.
                            All rights reserved.
                        </p>
                    </div>
                </Col>
            </Row>
        );
    };

    return (
        <footer className="footer py-6 bg-primary text-white">
            <Container>
                <Row>
                    {_renderBrand()}
                    {_renderUsefulLinks()}
                    {_renderServiceLinks()}
                    {_renderAddress()}
                </Row>
                {_renderHorizontal()}
                {_renderFooterCopyRight()}
            </Container>
        </footer>
    );
};

Footer.defaultProps = {
    footerBrandLink: "#root"
};
